import * as React from 'react';
import { ArrowDownward } from '@mui/icons-material'
import { Children, type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import type { ConversationInteractionsProps } from '../../types.js'
import { useConversationOptions } from '../ConversationOptionsProvider.js'
import { useScrollbarConfigureApi } from '../ScrollApiProvider.js'
import { ConversationsRoot } from './components/ConversationsRoot.js'
import { Root } from './components/Root.js'
import { ScrollToBottom } from './components/ScrollToBottom.js'
import { ScrollableArea } from './components/ScrollableArea.js'

export function ConversationInteractions(props: ConversationInteractionsProps) {
  const { testId, children, conversationEmpty } = props

  const { handleScrollToBottom, scrollbarVisible, showFab, scrollIsAtBottom, scrollerRef } = useReactToScroll()

  useReadFlow(scrollIsAtBottom, handleScrollToBottom, children)

  if (Children.count(children) === 0) return conversationEmpty ?? null

  return (
    <ConversationsRoot>
      <ScrollableArea ref={scrollerRef}>
        <Root data-testid={testId} data-tidal="ConversationInteractions" sx={{ pr: scrollbarVisible ? 1 : 2 }}>
          {children}
        </Root>
      </ScrollableArea>
      {showFab ? (
        <ScrollToBottom onScrollToBottom={handleScrollToBottom}>
          <ArrowDownward />
        </ScrollToBottom>
      ) : null}
    </ConversationsRoot>
  )
}

function useReactToScroll() {
  const scrollerRef = useRef<HTMLDivElement | null>(null)

  const [showFab, setShowFab] = useState(false)
  const scrollIsAtBottom = useRef(false)
  const autoScrollToBottomDone = useRef(false)
  const { preventScrollToBottomOnMount, scrollThresholdBeforeShowScrollToBottom, hideScrollToBottomFab } =
    useConversationOptions()
  const { registerScrollToBottom } = useScrollbarConfigureApi()

  const scrollbarVisible = scrollerRef.current
    ? scrollerRef.current.scrollHeight > scrollerRef.current.clientHeight
    : false

  const handleScrollToBottom = useCallback(() => {
    if (!scrollerRef.current) return

    scrollerRef.current.scrollTo({ top: scrollerRef.current.scrollHeight ?? 0 })
  }, [])

  useEffect(() => {
    const scroller = scrollerRef.current

    if (!scroller) return
    const controller = new AbortController()

    const observer = new ResizeObserver(_ => {
      const scrollQty = scroller.scrollTop + scroller.clientHeight

      scrollIsAtBottom.current = scrollQty >= scroller.scrollHeight
      setShowFab(scrollQty + scrollThresholdBeforeShowScrollToBottom <= scroller.scrollHeight)
    })

    observer.observe(scroller)

    scroller.addEventListener(
      'scroll',
      () => {
        const scrollQty = scroller.scrollTop + scroller.clientHeight

        scrollIsAtBottom.current = scrollQty >= scroller.scrollHeight
        setShowFab(scrollQty + scrollThresholdBeforeShowScrollToBottom <= scroller.scrollHeight)
      },
      { signal: controller.signal },
    )

    return () => {
      controller.abort()
      observer.disconnect()
    }
  }, [scrollThresholdBeforeShowScrollToBottom])

  useEffect(() => {
    const scroller = scrollerRef.current

    if (!scroller) return
    if (preventScrollToBottomOnMount) return

    if (autoScrollToBottomDone.current) return
    autoScrollToBottomDone.current = true

    // calling scrollTo inside a requestAnimationFrame
    // ensure that the scroll is done after the component is mounted
    // also useLayoutEffect was not achieving the desired effect
    requestAnimationFrame(() => {
      scroller.scrollTo({ top: scroller.scrollHeight ?? 0 })
    })
  }, [preventScrollToBottomOnMount])

  useEffect(() => {
    registerScrollToBottom(handleScrollToBottom)
  }, [registerScrollToBottom, handleScrollToBottom])

  return {
    showFab: hideScrollToBottomFab ? false : showFab,
    scrollbarVisible,
    handleScrollToBottom,
    scrollIsAtBottom,
    scrollerRef,
  }
}

/**
 * Handles the read flow behavior.
 * Forcing the scroll to the bottom when new messages are added and the user wants to maintain the read flow.
 */
function useReadFlow(
  scrollIsAtBottom: MutableRefObject<boolean>,
  handleScrollToBottom: () => void,
  interactionElements: unknown,
) {
  const { disableAutoReadFlow } = useConversationOptions()

  useEffect(() => {
    if (disableAutoReadFlow) return

    if (scrollIsAtBottom.current) {
      handleScrollToBottom()
    }

    // we want to run the effect any time `interactionElements` changes,
    // but we need to add it here to avoid biome to complain that it is not been used
    interactionElements
  }, [
    disableAutoReadFlow,
    interactionElements,
    handleScrollToBottom,
    // this is a mutable ref, but biome is not able to recognize it, adding it as a dependency is harmless
    scrollIsAtBottom,
  ])
}
